import { z } from 'zod';
import type {
    AcceptLegalDocumentRequest,
    CreateLegalDocumentRequest,
    CreateLegalDocumentResponse,
    GetLegalDocumentResponse,
    LegalDocumentsSearchRequest,
    LegalDocumentListItem,
    ListPendingRequiredLegalDocumentsResponse,
    SearchLegalDocumentsPageResponse,
} from '../legal-documents.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import { NonEmptyStringSchema } from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';

export const LegalDocumentUuidSchema: z.ZodType<string> = z.string().uuid();

export const LegalDocumentUuidParamSchema: z.ZodType<string> = LegalDocumentUuidSchema;

export const LegalDocumentsOpaqueCursorQuerySchema: z.ZodType<string | undefined> = z
    .string()
    .trim()
    .min(1)
    .optional();

export const LegalDocumentsSearchRequestSchema: z.ZodType<LegalDocumentsSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
    })
    .strict();

export const LegalDocumentListItemSchema = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
    legal_document_title: NonEmptyStringSchema,
    legal_document_version: NonEmptyStringSchema,
    legal_document_content_type: NonEmptyStringSchema,
    in_force_since: z.string().datetime(),
    in_force_to: z.string().datetime().nullable(),
    required_for_users: z.boolean(),
    required_for_orgs: z.boolean(),
    content_sha256: NonEmptyStringSchema,
    source_filename: z.string().optional(),
    content_length_bytes: z.number().int().nonnegative(),
}) satisfies z.ZodType<LegalDocumentListItem>;

export const SearchLegalDocumentsPageResponseSchema = z.object({
    legal_documents: z.array(LegalDocumentListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
}) satisfies z.ZodType<SearchLegalDocumentsPageResponse>;

export const CreateLegalDocumentRequestSchema = z.object({
    legal_document_title: NonEmptyStringSchema,
    legal_document_version: NonEmptyStringSchema,
    legal_document_text: NonEmptyStringSchema,
    legal_document_content_type: NonEmptyStringSchema,
    in_force_since: z.string().datetime(),
    in_force_to: z.string().datetime().nullable().optional(),
    required_for_users: z.boolean(),
    required_for_orgs: z.boolean(),
    source_filename: z.string().optional(),
}) satisfies z.ZodType<CreateLegalDocumentRequest>;

export const CreateLegalDocumentResponseSchema = z.object({
    legal_document_uuid: LegalDocumentUuidSchema,
}) satisfies z.ZodType<CreateLegalDocumentResponse>;

export const GetLegalDocumentResponseSchema = z.object({
    legal_document: LegalDocumentListItemSchema.extend({
        legal_document_text: NonEmptyStringSchema,
    }),
}) satisfies z.ZodType<GetLegalDocumentResponse>;

export const ListPendingRequiredLegalDocumentsResponseSchema = z.object({
    legal_documents: z.array(LegalDocumentListItemSchema),
}) satisfies z.ZodType<ListPendingRequiredLegalDocumentsResponse>;

export const AcceptLegalDocumentRequestSchema = z.object({
    accepted_role_assignment_uuid: z.string().uuid().optional(),
}) satisfies z.ZodType<AcceptLegalDocumentRequest>;
